import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import "./../styles/NavigationBar.css";
import { useAuth } from './../context/AuthContext';
import 'bootstrap-icons/font/bootstrap-icons.css';
import SupportModal from './modals/SupportModal';
import { useLayout } from './../context/LayoutContext';
import phonePng from './../assets/images/phone-25.png';
import menuToggle from './../assets/images/menu-35.png';
import homePng from './../assets/images/home-50.png';
import applePng from './../assets/images/apple-50.png';
import androidPng from './../assets/images/android-50.png';
import chromePng from './../assets/images/chrome-50.png';
import questionPng from './../assets/images/question-50.png';
import windowsPng from './../assets/images/windows-50.png';
import logoutPng from './../assets/images/logout-50.png';
import logoPng from './../assets/images/logo.png';
import backArrowPng from './../assets/images/return-50.png';
import notificationService from './../services/NotificationService';
import teamviewer from './../assets/images/teamviewer-white-50.png';

function NavigationBar() {
  const { toggleNav } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true); // Default to expanded state
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [canGoBack, setCanGoBack] = useState(false);
  const infoRef = useRef(null);
  const TEAMVIEWER_WINDOWS = process.env.REACT_APP_TEAMVIEWER_WINDOWS;
  const TEAMVIEWER_MAC = process.env.REACT_APP_TEAMVIEWER_MAC;

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await notificationService.getNotifications();
        setNotifications(data.filter(notification => notification.visible === 1));
      } catch (error) {
        console.error('Failed to fetch notifications');
      }
    };

    fetchNotifications();
    setCanGoBack(window.history.length > 1);
  }, []);

  useEffect(() => {
    setCanGoBack(window.history.length > 1);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate(`/login`);
  };

  const handleReturn = () => {
    if (canGoBack) {
      navigate(-1);
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(prev => !prev);
  };

   const openModal = () => {
    setModalOpen(true);
    setShowDropdown(false);
  };

  const closeModal = () => setModalOpen(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (infoRef.current && !infoRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [showDropdown]);

  const toggleMenu = () => {
    setIsExpanded(prevState => !prevState);
    toggleNav();
  };

const isMobile = (() => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const uaMobile = /Android|iPhone|iPad|iPod/i.test(ua);
  const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const narrow = window.innerWidth < 1024;

  return uaMobile || (coarse && narrow);
})();

// Support both /android and /esafe/android (and similar)
const androidPaths = [
  '/android',
  '/esafe/android',
  '/visorID',
  '/esafe/visorID',
  '/install',
  '/esafe/install',
  '/android-setup',
  '/esafe/android-setup',
];

const isAndroidRoute = androidPaths.some(p => {
  if (location.pathname === p) return true;
  return location.pathname.startsWith(`${p}/`);
});

const isAndroidPage = isAndroidRoute && isMobile;

  return (
    <div className={!isAndroidPage ? "navigation-container" : "navigation-mobile-container"}>
      {!isAndroidPage && (
        <aside className={`vertical-menu ${isExpanded ? 'expanded' : 'collapsed'}`}>
          <NavLink onClick={toggleMenu} className="menu-toggle">
            <img src={menuToggle} alt="Toggle Menu" />
          </NavLink>
          <ul className="nav-menu-left">
            {canGoBack && ( // Only show the return button if there is a page to go back to
              <>
                <li className="nav-item">
                  <button onClick={handleReturn} className="nav-link return-button">
                    <img src={backArrowPng} alt="Return" className="nav-icon" />
                    {isExpanded && 'Return'}
                  </button>
                </li>
                <hr className="menu-divider" />
              </>
            )}
            <li className="nav-item">
              {notifications.length > 0 && <span className="notification-badge">{notifications.length}</span>}
              <NavLink to="/notification" className={({ isActive }) => isActive ? "nav-link active-nav-link" : "nav-link"}>
                <div className="home-icon-container">
                  <img src={homePng} alt="Notifications" className='nav-icon' />
                </div>
                {isExpanded && 'Notifications'}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink
                to="/windows"
                className={({ isActive }) => isActive ? "nav-link active-nav-link" : "nav-link"}
              >
                <img src={windowsPng} alt="Windows" className='nav-icon' />
                {isExpanded && 'Windows'}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink
                to="/mac"
                className={({ isActive }) => isActive ? "nav-link active-nav-link" : "nav-link"}
              >
                <img src={applePng} alt="Apple" className='nav-icon' />
                {isExpanded && 'Apple'}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink
                to="/chrome"
                className={({ isActive }) => isActive ? "nav-link active-nav-link" : "nav-link"}
              >
                <img src={chromePng} alt="Chrome" className='nav-icon' />
                {isExpanded && 'Chrome'}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink
                to="/android"
                className={({ isActive }) => isActive ? "nav-link active-nav-link" : "nav-link"}
              >
                <img src={androidPng} alt="Android" className='nav-icon' />
                {isExpanded && 'Android'}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink
                to="/supports"
                className={({ isActive }) => isActive ? "nav-link active-nav-link" : "nav-link"}
              >
                <img src={questionPng} alt="Support" className='nav-icon' />
                {isExpanded && 'Support'}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink
                to="/logout"
                className={({ isActive }) => isActive ? "nav-link active-nav-link" : "nav-link"}
                onClick={handleLogout}
              >
                <img src={logoutPng} alt="Logout" className='nav-icon' />
                {isExpanded && 'Logout'}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink
              to={TEAMVIEWER_WINDOWS}
              className={({ isActive }) => isActive ? "nav-link active-nav-link" : "nav-link"}>
              <img src={teamviewer} alt="Support" className='nav-icon' />
              {isExpanded && 'TV Windows'}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink
              to={TEAMVIEWER_MAC}
              className={({ isActive }) => isActive ? "nav-link active-nav-link" : "nav-link"}>
              <img src={teamviewer} alt="Support" className='nav-icon' />
              {isExpanded && 'TV Mac'}
              </NavLink>
            </li>
          </ul>
        </aside>
      )}
      <div className={isAndroidPage ? "navbar-mobile-container" : "navbar-container"}>
        <nav className={isAndroidPage ? "mobile-navbar" : "navbar"}>
          <div className={isAndroidPage ? "navbar-logo-android" : "navbar-logo-container"}>
            <a href="/esafe/notification">
              <img src={logoPng} alt="Logo" className={isExpanded ? "navbar-logo" : "navbar-logo-collapsed"} />
            </a>
          </div>
          <div ref={infoRef} className={isAndroidPage ? "navbar-info-mobile" : "navbar-info"}>
            {isAndroidPage && (
              <div className="phone-handle">
                <a href="tel:+123456789">
                  <img src={phonePng} alt="Phone" />
                </a>
              </div>
            )}
            {isAndroidPage && <button className={'nav-mobile-button-logout'} onClick={handleLogout}>Sign Out</button> }
            <div className='opening-container'>
            {!isAndroidPage && (
              <div className="opening-hours">
                <p className="hours-text">Mon - Fri (excl. Bank Holidays)</p>
                <p className="hours-time">09:00 - 17:00</p>
              </div>
            )}
            </div>
            <button onClick={toggleDropdown} className="dropdown-toggle">
              <i className="bi bi-grid"></i>
            </button>
            {showDropdown && (
              <ul className="dropdown-menu">
                <li><button className="nav-button-support" onClick={openModal}>Support</button></li>
                <li><button className='nav-button-logout' onClick={handleLogout}>Sign Out</button></li>
              </ul>
            )}
          </div>
          <SupportModal isOpen={modalOpen} onClose={closeModal} />
        </nav>
      </div>
    </div>
  );
}

export default NavigationBar;
